import { ClickAwayListener, Typography } from '@mui/material'
import { styled } from '@mui/system'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { ExpandLess } from '@mui/icons-material'
import { useCallback, useRef, useState } from 'react'
import { colorPalette } from '../../../../style/colorPalette'
import {
  TextMatchCondition,
  TextMatchMode,
  TextMatchQuantifier,
} from '../../../../../domain/value-object/WbsItemSearchConditionV2Vo'
import { SelectOption } from '../../../../model/selectOption'
import { Button } from '../../../../components/buttons'
import WbsItemSearchCheckboxSelectConditionPopper from './WbsItemSearchTypesCheckboxSelectCondition'
import GroupedChipList from './GroupedChipList'

type Props = {
  title: string
  value: TextMatchCondition[] | undefined
  options: SelectOption[]
  onChange: (value: TextMatchCondition[] | undefined) => void
  includeQuantifier?: TextMatchQuantifier
  excludeQuantifier?: TextMatchQuantifier
  onToggleIncludeQuantifier?: () => void
  onToggleExcludeQuantifier?: () => void
}

const SearchConditionChipSelect = ({
  title,
  value,
  options,
  onChange,
  includeQuantifier,
  excludeQuantifier,
  onToggleIncludeQuantifier,
  onToggleExcludeQuantifier,
}: Props) => {
  const [openPopper, setOpenPopper] = useState(false)
  const anchorRef = useRef<HTMLSpanElement>(null)

  const toggleChip = useCallback(
    (index: number) => {
      if (!value) return
      const next = value.map((c, i) =>
        i === index
          ? {
              ...c,
              mode: (c.mode === 'include'
                ? 'exclude'
                : 'include') as TextMatchMode,
            }
          : c
      )
      onChange(next)
    },
    [value, onChange]
  )

  const removeChip = useCallback(
    (index: number) => {
      if (!value) return
      const next = value.filter((_, i) => i !== index)
      onChange(next.length > 0 ? next : undefined)
    },
    [value, onChange]
  )

  const clearAll = useCallback(() => {
    onChange(undefined)
  }, [onChange])

  const onCheckboxChange = useCallback(
    (newValue: string[] | undefined) => {
      if (!newValue || newValue.length === 0) {
        onChange(undefined)
        return
      }
      const existingModes = new Map((value ?? []).map(c => [c.value, c.mode]))
      const conditions: TextMatchCondition[] = newValue.map(v => ({
        value: v,
        mode: existingModes.get(v) ?? 'include',
      }))
      onChange(conditions)
    },
    [value, onChange]
  )

  const selectedUuids = value?.map(c => c.value)

  const getOptionName = useCallback(
    (uuid: string) => options.find(o => o.value === uuid)?.name ?? uuid,
    [options]
  )

  const handlePopperClose = useCallback(() => {
    setOpenPopper(false)
  }, [])

  const hasChips = value && value.length > 0

  return (
    <ClickAwayListener onClickAway={handlePopperClose}>
      <div>
        <SelectRow hasChips={!!hasChips}>
          <Typography
            sx={{
              color: colorPalette.monotone[4],
              minWidth: '80px',
              flexShrink: 0,
            }}
          >
            {title}
          </Typography>
          <PopperAnchorContainer>
            <span ref={anchorRef}>
              <Button
                colorPattern="monotone"
                size="s"
                sx={{
                  borderRadius: '15px',
                  border: `1px dashed ${colorPalette.monotone[2]}`,
                  background: colorPalette.monotone[0],
                  color: colorPalette.monotone[3],
                  cursor: 'pointer',
                }}
                onClick={() => setOpenPopper(prev => !prev)}
              >
                {openPopper ? (
                  <ExpandLess sx={{ height: '16px', width: '16px' }} />
                ) : (
                  <AddRoundedIcon sx={{ height: '12px', width: '12px' }} />
                )}
              </Button>
            </span>
            {openPopper && (
              <WbsItemSearchCheckboxSelectConditionPopper
                open={openPopper}
                anchorEl={anchorRef.current || undefined}
                options={options}
                value={selectedUuids}
                onChange={onCheckboxChange}
                offset={20}
                placement="bottom-start"
                maxHeight="300px"
              />
            )}
          </PopperAnchorContainer>
          <ClearButton onClick={clearAll}>
            <ClearRoundedIcon sx={{ color: colorPalette.monotone[4] }} />
          </ClearButton>
        </SelectRow>
        {hasChips && (
          <GroupedChipList
            value={value}
            onToggle={toggleChip}
            onDelete={removeChip}
            getLabel={c => getOptionName(c.value)}
            includeQuantifier={includeQuantifier}
            excludeQuantifier={excludeQuantifier}
            onToggleIncludeQuantifier={onToggleIncludeQuantifier}
            onToggleExcludeQuantifier={onToggleExcludeQuantifier}
            sx={{
              padding: '6px 10px 6px 110px',
              borderLeft: `1px solid ${colorPalette.monotone[2]}`,
              borderRight: `1px solid ${colorPalette.monotone[2]}`,
              borderBottom: `1px solid ${colorPalette.monotone[2]}`,
              borderRadius: '0 0 4px 4px',
              backgroundColor: colorPalette.monotone[0],
            }}
          />
        )}
      </div>
    </ClickAwayListener>
  )
}

const SelectRow = styled('div', {
  shouldForwardProp: prop => prop !== 'hasChips',
})<{ hasChips: boolean }>(({ hasChips }) => ({
  minHeight: '40px',
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: hasChips ? '4px 4px 0 0' : '4px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '5px 10px',
}))

const PopperAnchorContainer = styled('div')({
  marginLeft: '20px',
})

const ClearButton = styled('button')({
  minWidth: '20px',
  margin: '0 0 0 auto',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px',
  display: 'flex',
  alignItems: 'center',
})

export default SearchConditionChipSelect
